import React from "react";
import { Link } from "gatsby";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import GoogleMap from "@/components/GoogleMap";
import NearbySuburbs from "@/components/NearbySuburbs";
import RelatedServiceLinks from "@/components/RelatedServiceLinks";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { locationConfigs } from "@/data/locationData";
import { BOOKING_URL, LINK_ATTRIBUTES } from "@/lib/config";
import {
    MapPin,
    CheckCircle,
    Users,
    Heart,
    Shield,
    Star,
    Award,
    Smile,
    Baby
} from "lucide-react";

const greenvaleFaqItems = [
    {
        question: "How far is Y3 Smiles Dental from Greenvale?",
        answer: "About 12 to 15 minutes from Greenvale via Mickleham Road. We are at Unit 1, 34 King William Street, Broadmeadows VIC 3047."
    },
    {
        question: "Do you offer same-day emergency dental for Greenvale patients?",
        answer: "Yes. We reserve emergency slots each day for severe pain, broken teeth, knocked-out teeth, swelling and trauma. Call (03) 9022 4442 and we will do our best to see you the same day."
    },
    {
        question: "Do you bulk-bill children through CDBS?",
        answer: "Yes. Eligible children aged 0-17 can receive up to $1,158 in dental services every two calendar years under the Medicare CDBS. We check eligibility, bulk-bill the benefit, and there is no out-of-pocket cost for covered treatments."
    },
    {
        question: "Is there a Turkish-speaking dentist for Greenvale patients?",
        answer: "Yes. Our principal dentist Dr Fatima Kurnaz is bilingual in English and Turkish."
    },
    {
        question: "Do you have an Arabic-speaking team member?",
        answer: "Yes. Our reception and clinical team includes Arabic-speaking staff. Just let us know when booking and we will arrange your appointment with the right person."
    },
    {
        question: "What health funds do you accept?",
        answer: "All major Australian health funds with HICAPS instant claiming on-site, including Bupa, Medibank Private, HCF, NIB (we are a NIB First Choice provider), CBHS, AHM, HBF and others."
    },
    {
        question: "What are your opening hours?",
        answer: "Monday to Friday 9:00 AM - 5:30 PM, Saturday 9:00 AM - 1:00 PM, closed Sunday."
    },
    {
        question: "Is parking free at the Broadmeadows clinic?",
        answer: "Yes. Free parking is available directly in front of the clinic on King William Street."
    },
    {
        question: "How do I get to your clinic from Greenvale by public transport?",
        answer: "Take a bus or train connection toward Broadmeadows Station, then walk along King Street to the clinic."
    },
    {
        question: "Do you offer dental implants for Greenvale patients?",
        answer: "Yes. Single-tooth implants are from $4,000 all-inclusive, and full-arch (All-on-X) treatments from $24,500 per arch. Dr Pavel provides a free initial consultation including a written quote."
    },
    {
        question: "What payment plans are available?",
        answer: "Interest-free payment plans are available up to 12 months on treatments over $300, with extended low-interest plans up to 24 months. We also accept EFTPOS, Visa, Mastercard, and in some cases ATO Compassionate Release of Superannuation can be used to fund clinically-required dental care."
    },
    {
        question: "Do you accept walk-ins or do I need to book?",
        answer: "Walk-ins are welcome for emergency appointments when our schedule allows, but for everything else we strongly recommend booking by phone (03) 9022 4442 or online to guarantee your preferred time."
    }
] as const;

const greenvaleFaqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: greenvaleFaqItems.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
            "@type": "Answer",
            text: item.answer
        }
    }))
};

const greenvaleServices = [
    {
        category: "Family Dentistry",
        items: ["Fillings", "Dentures", "Extractions", "Root canal treatments", "TMJ support for jaw discomfort"]
    },
    {
        category: "Children's Dentistry",
        items: ["Fluoride applications", "Oral hygiene education", "Fissure sealants", "Custom-fitted mouthguards"]
    },
    {
        category: "Cosmetic Dentistry",
        items: ["Veneers", "Teeth whitening", "Crowns", "Reshaping teeth", "Full-mouth rehabilitation"]
    },
    {
        category: "Gentle Care Options",
        items: ["Designed for patients who feel anxious", "Nervous about dental visits", "Comfort-focused approach"]
    },
    {
        category: "Emergency Dentistry",
        items: ["Prompt care for unexpected pain", "Injury treatment", "Urgent dental needs"]
    }
] as const;

const greenvalePatientReasons = [
    {
        icon: MapPin,
        title: "About 12-15 minutes away",
        desc: "Greenvale patients can usually reach our Broadmeadows clinic in about 12 to 15 minutes via Mickleham Road."
    },
    {
        icon: Users,
        title: "Turkish-speaking principal dentist",
        desc: "Dr Fatima Kurnaz speaks Turkish and English, which helps many Greenvale families feel comfortable discussing treatment."
    },
    {
        icon: Baby,
        title: "CDBS bulk-billed for kids",
        desc: "Eligible children aged 0-17 can use Medicare CDBS for covered treatment with no out-of-pocket cost."
    },
    {
        icon: CheckCircle,
        title: "Free on-site parking",
        desc: "Parking is available directly in front of the clinic, so parents and older patients do not need to hunt for street parking."
    },
    {
        icon: Award,
        title: "Open six days",
        desc: "Monday to Saturday access helps working families, shift workers and school-age children find a time that suits them."
    },
    {
        icon: Shield,
        title: "Transparent pricing",
        desc: "We write down the treatment plan and cost before work starts, so Greenvale patients know what to expect."
    }
] as const;

const greenvaleTestimonials = [
    "We came from Greenvale for a family check-up and the drive was straightforward. The team were warm and explained everything clearly.",
    "Our son's CDBS visit was bulk-billed and the whole appointment felt calm and easy. That matters when you're juggling a family schedule.",
    "The implant consult was thorough, and the quote was explained in plain language. I left knowing exactly what to expect."
];

const greenvaleLandmarks = [
    {
        icon: "🛍️",
        title: "Broadmeadows Central",
        description: "A useful stop if you are combining a dental visit with shopping or groceries."
    },
    {
        icon: "🏛️",
        title: "Hume Civic Centre",
        description: "A familiar Broadmeadows landmark for locals heading into the area for other errands."
    },
    {
        icon: "🚆",
        title: "Broadmeadows Train Station",
        description: "A short walk from the station makes the clinic easy to reach without driving."
    },
    {
        icon: "🛣️",
        title: "Hume Freeway (M31)",
        description: "The Hume Freeway connects directly to Broadmeadows for drivers coming from Greenvale."
    }
] as const;

const GreenvalePage = () => {
    useScrollAnimation();

    return (
        <div className="min-h-screen flex flex-col">
            <SEO
              title="Dentist Greenvale | Y3 Smiles Dental — 12-15 min via Mickleham Rd"
              description="Greenvale families trust Y3 Smiles Dental near Greenvale for same-day emergency care, CDBS kids dental, Turkish and Arabic support, and free parking."
              canonicalUrl="https://y3smilesdental.com.au/locations/greenvale/"
            >
              <script type="application/ld+json">{JSON.stringify(greenvaleFaqSchema)}</script>
            </SEO>
            <Header />
            <main className="flex-grow">
                <section className="relative bg-gradient-to-br from-neutral-50 via-primary/5 to-neutral-100 py-32 overflow-hidden">
                    <div className="absolute inset-0 opacity-5">
                        <div className="absolute top-10 left-10 w-32 h-32 rounded-full bg-primary"></div>
                        <div className="absolute top-40 right-20 w-20 h-20 rounded-full bg-secondary"></div>
                        <div className="absolute bottom-20 left-1/4 w-16 h-16 rounded-full bg-primary"></div>
                        <div className="absolute bottom-32 right-1/3 w-24 h-24 rounded-full bg-secondary"></div>
                    </div>

                    <div className="container mx-auto px-6 relative z-10">
                        <div className="max-w-5xl mx-auto">
                            <div className="text-center mb-12">
                                <div className="inline-flex items-center justify-center mb-8 p-4 bg-white/90 backdrop-blur-sm rounded-2xl shadow-soft">
                                    <img src="/location.png" alt="Location" className="w-10 h-10 mr-4" />
                                    <div className="text-left">
                                        <p className="text-sm font-medium text-primary uppercase tracking-wider">Dentist near Greenvale</p>
                                        <p className="text-lg font-bold text-neutral-800">Greenvale</p>
                                    </div>
                                </div>

                                <h1 className="text-6xl md:text-7xl font-bold text-neutral-800 mb-6 leading-tight">
                                    Dentist Serving{" "}
                                    <span className="block text-primary">Greenvale</span>
                                </h1>

                                <p className="text-xl text-neutral-800 mb-12 leading-relaxed max-w-3xl mx-auto">
                                    Y3 Smiles Dental looks after Greenvale families from our Broadmeadows clinic, about 12 to 15 minutes away via Mickleham Road. We keep same-day emergency slots available, bulk-bill eligible children through Medicare CDBS, and provide Turkish and Arabic language support in a calm, family-friendly setting.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
                                <div className="flex items-center bg-white/90 backdrop-blur-sm rounded-2xl px-6 py-4 shadow-soft hover:shadow-medium transition-all duration-300 group">
                                    <CheckCircle className="w-6 h-6 text-primary mr-3 group-hover:scale-110 transition-transform" />
                                    <span className="font-semibold text-neutral-800">12-15 Min from Greenvale</span>
                                </div>
                                <div className="flex items-center bg-white/90 backdrop-blur-sm rounded-2xl px-6 py-4 shadow-soft hover:shadow-medium transition-all duration-300 group">
                                    <Award className="w-6 h-6 text-primary mr-3 group-hover:scale-110 transition-transform" />
                                    <span className="font-semibold text-neutral-800">Turkish-Speaking Dentist</span>
                                </div>
                                <div className="flex items-center bg-white/90 backdrop-blur-sm rounded-2xl px-6 py-4 shadow-soft hover:shadow-medium transition-all duration-300 group">
                                    <Heart className="w-6 h-6 text-primary mr-3 group-hover:scale-110 transition-transform" />
                                    <span className="font-semibold text-neutral-800">CDBS Bulk Billed</span>
                                </div>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                                <Button variant="booking" size="xl" asChild className="group">
                                    <a href={BOOKING_URL} {...LINK_ATTRIBUTES}>
                                        <Smile className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                                        BOOK APPOINTMENT
                                    </a>
                                </Button>
                                <Button variant="booking" size="xl" asChild>
                                    <Link to="/treatments">
                                        View Our Treatments
                                    </Link>
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-32 bg-gradient-to-b from-white to-neutral-50 relative">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-secondary to-primary opacity-20"></div>

                    <div className="container mx-auto px-6">
                        <div className="max-w-7xl mx-auto">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                                <div className="space-y-8">
                                    <div className="space-y-4">
                                        <h2 className="text-2xl font-semibold text-primary uppercase tracking-widest mb-4">
                                            About Us
                                        </h2>
                                        <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 leading-tight">
                                            About Y3 Smiles Dental —
                                            <span className="block text-primary">Your Dentist near Greenvale</span>
                                        </h2>
                                    </div>

                                    <div className="space-y-6">
                                        <div className="bg-white p-6 rounded-2xl shadow-soft border-l-4 border-primary">
                                            <p className="text-lg text-neutral-700 leading-relaxed">
                                                Y3 Smiles Dental is a modern family practice founded by Dr Fatima Kurnaz, DDS (University of Melbourne). For Greenvale patients, our Broadmeadows clinic is the nearest full-service private dental option, which keeps the trip manageable while still giving you access to a broad range of treatments under one roof.
                                            </p>
                                        </div>

                                        <div className="bg-gradient-to-r from-primary/5 to-secondary/5 p-6 rounded-2xl">
                                            <p className="text-lg text-neutral-700 leading-relaxed">
                                                Our team includes Dr Pinar Geyik for preventive and cosmetic care, plus Dr Pavel for implants and oral surgery. That combination is useful for Greenvale families who want routine check-ups, children's dentistry and more advanced restorative work handled in one place.
                                            </p>
                                        </div>

                                        <div className="bg-primary/10 p-6 rounded-2xl border border-primary/20">
                                            <p className="text-lg text-primary font-semibold leading-relaxed">
                                                We explain each step clearly, provide transparent quotes before treatment begins, and keep the clinic calm for anxious patients, parents and children alike.
                                            </p>
                                        </div>

                                        <div className="bg-white p-6 rounded-2xl shadow-soft border border-primary/5">
                                            <p className="text-lg text-neutral-700 leading-relaxed">
                                                Greenvale families often juggle long commutes, school commitments and weekend activities, so the clinic needs to feel easy to access and easy to understand. We spend time on practical details such as what to bring, how long a visit will take and what the next step will be after the appointment. That keeps the process calm for parents who want straightforward care without unnecessary detours.
                                            </p>
                                        </div>

                                        <div className="bg-white p-6 rounded-2xl shadow-soft border border-primary/5">
                                            <p className="text-lg text-neutral-700 leading-relaxed">
                                                Because Greenvale is a community where many families plan carefully around work and school runs, it helps to have a clinic that keeps things simple. We aim to give clear directions, realistic time estimates and direct communication so the appointment fits into the rest of the day instead of creating extra stress. That practical style matters just as much as the treatment itself.
                                            </p>
                                        </div>

                                        <div className="bg-white p-6 rounded-2xl shadow-soft border border-primary/5">
                                            <p className="text-lg text-neutral-700 leading-relaxed">
                                                Greenvale patients also tend to appreciate a clinic that can handle both routine and more advanced care without sending them back and forth between different providers. If you start with a basic appointment and later decide you want whitening, an implant discussion or a restorative plan, we can keep the same notes, same context and same relationship in place so the care feels joined up.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex flex-col sm:flex-row gap-4">
                                        <Button variant="booking" size="lg" asChild className="group">
                                            <a href={BOOKING_URL} {...LINK_ATTRIBUTES}>
                                                <CheckCircle className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                                                BOOK CONSULTATION
                                            </a>
                                        </Button>
                                        <Button variant="booking" size="lg" asChild>
                                            <Link to="#services">Our Services</Link>
                                        </Button>
                                    </div>
                                </div>

                                <div className="relative">
                                    <div className="absolute -top-6 -left-6 w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 rounded-3xl"></div>
                                    <div className="relative bg-white p-2 rounded-3xl shadow-2xl">
                                        <div className="aspect-[4/3] bg-neutral-100 rounded-2xl overflow-hidden">
                                            <img
                                                src="/greenvale.png"
                                                alt="Happy family at dental clinic near Greenvale"
                                                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section id="services" className="py-32 bg-gradient-to-b from-white to-neutral-50 relative">
                    <div className="container mx-auto px-6">
                        <div className="max-w-7xl mx-auto">
                            <div className="text-center mb-20">
                                <h2 className="text-2xl font-semibold text-primary uppercase tracking-widest mb-4">
                                    Our Services
                                </h2>

                                <h2 className="text-4xl md:text-5xl font-bold text-neutral-800 mb-6 leading-tight">
                                    Dental Services Available
                                    <span className="block text-primary">to Greenvale Residents</span>
                                </h2>

                                <p className="text-xl text-neutral-800 mb-8 max-w-4xl mx-auto leading-relaxed">
                                    Greenvale patients access the full range of treatments at Y3 Smiles Dental, from family check-ups and children's CDBS care through to cosmetic makeovers, implants and same-day emergency appointments, all planned with clear fees and a patient-first approach.
                                </p>
                                <p className="text-lg text-neutral-700 max-w-4xl mx-auto leading-relaxed">
                                    Because Greenvale is a little farther out than some of the northern suburbs we serve, many patients prefer to combine dentistry with one tidy trip into Broadmeadows instead of spreading appointments across multiple clinics. That works well for siblings, for parents bringing a child and a grandparent together, and for adults who want preventive care, treatment and follow-up booked in a practical sequence.
                                </p>
                                <p className="text-lg text-neutral-700 max-w-4xl mx-auto leading-relaxed">
                                    The result is a dental visit that feels planned rather than improvised. Greenvale patients often tell us they appreciate knowing the route, the likely appointment length and the next review before they even leave the house, because that makes it easier to fit dentistry around the rest of the week.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16 max-w-6xl mx-auto">
                                {greenvaleServices.map((service, index) => (
                                    <div key={index} className="group bg-white p-8 rounded-3xl shadow-soft hover:shadow-xl transition-all duration-300 border border-primary/5 hover:border-primary/20 relative overflow-hidden">
                                        <div className="absolute inset-0 bg-gradient-to-br from-primary/0 to-secondary/0 group-hover:from-primary/5 group-hover:to-secondary/5 transition-all duration-300"></div>

                                        <div className="relative z-10">
                                            <div className="flex items-center mb-6">
                                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
                                                    {service.category === "Family Dentistry" && <Users className="w-6 h-6 text-primary" />}
                                                    {service.category === "Children's Dentistry" && <Baby className="w-6 h-6 text-primary" />}
                                                    {service.category === "Cosmetic Dentistry" && <Star className="w-6 h-6 text-primary" />}
                                                    {service.category === "Gentle Care Options" && <Heart className="w-6 h-6 text-primary" />}
                                                    {service.category === "Emergency Dentistry" && <Shield className="w-6 h-6 text-primary" />}
                                                </div>
                                                <div className="w-8 h-1 bg-gradient-to-r from-primary to-secondary rounded-full"></div>
                                            </div>

                                            <h3 className="text-xl font-bold text-neutral-800 mb-4 group-hover:text-primary transition-colors duration-300">
                                                {service.category}
                                            </h3>

                                            <ul className="space-y-3">
                                                {service.items.map((item, itemIndex) => (
                                                    <li key={itemIndex} className="flex items-start">
                                                        <CheckCircle className="w-5 h-5 text-primary mr-3 mt-0.5 flex-shrink-0" />
                                                        <span className="text-neutral-800 leading-relaxed">{item}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-secondary transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                                    </div>
                                ))}
                            </div>

                            <div className="bg-gradient-to-r from-primary/10 via-white to-secondary/10 p-12 rounded-3xl border border-primary/20 text-center">
                                <h3 className="text-3xl font-bold text-neutral-800 mb-4">
                                    Ready for family-focused dental care?
                                </h3>
                                <p className="text-lg text-neutral-800 mb-8 max-w-2xl mx-auto">
                                    Experience comprehensive dental services designed for every member of your family in a welcoming, comfortable environment.
                                </p>

                                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                                    <Button variant="booking" size="xl" asChild className="group">
                                        <a href={BOOKING_URL} {...LINK_ATTRIBUTES}>
                                            <Smile className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform" />
                                            BOOK FAMILY CONSULTATION
                                        </a>
                                    </Button>
                                    <Button variant="booking" size="xl" asChild>
                                        <Link to="/treatments">
                                            <Shield className="w-5 h-5 mr-2" />
                                            VIEW ALL TREATMENTS
                                        </Link>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-20 bg-white">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-6 text-center">
                                Children's Dentistry &amp; CDBS Bulk Billing for Greenvale Families
                            </h2>
                            <div className="bg-primary/5 rounded-2xl p-8 border border-primary/10 mb-8 space-y-4">
                                <p className="text-lg text-neutral-700 leading-relaxed">
                                    Eligible children aged 0-17 can receive up to <strong>$1,158 in dental services every two calendar years</strong> under the Medicare Child Dental Benefits Schedule (CDBS). At Y3 Smiles Dental we are an approved CDBS provider, we check eligibility for you, and we <strong>bulk-bill</strong> the benefit so there is <strong>no out-of-pocket cost</strong> for covered treatments.
                                </p>
                                <p className="text-lg text-neutral-700 leading-relaxed">
                                    Covered treatments include check-ups, cleans, X-rays, fluoride applications, fissure sealants, fillings and extractions. That means Greenvale parents can keep their children's preventive care up to date without worrying about surprise costs.
                                </p>
                                <p className="text-lg text-neutral-700 leading-relaxed">
                                    For nervous children, we keep the first visit slow and friendly, explain each instrument in plain language, and use a calm chairside style so the appointment feels manageable from start to finish.
                                </p>
                                <p className="text-lg text-neutral-700 leading-relaxed">
                                    That extra care matters for Greenvale families whose children may be starting school, moving into sport or dealing with their first fillings. A steady first experience can shape how a child feels about dentistry for years, so we focus on trust, routine and clear expectations rather than rushing through the visit.
                                </p>
                            </div>
                            <div className="text-center">
                                <Button variant="booking" size="lg" asChild>
                                    <Link to="/treatments/childrens-dentistry">
                                        <Baby className="w-5 h-5 mr-2" />
                                        Learn About Children's Dentistry
                                    </Link>
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-20 bg-neutral-50">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-6 text-center">
                                Same-Day Emergency Dental for Greenvale
                            </h2>
                            <div className="grid md:grid-cols-2 gap-6 mb-8">
                                <div className="bg-white rounded-2xl p-6 shadow-soft border-l-4 border-primary">
                                    <h3 className="text-lg font-semibold text-neutral-800 mb-3">We treat dental emergencies including:</h3>
                                    <ul className="space-y-2 text-neutral-700">
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Severe toothache</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Knocked-out or broken tooth</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Broken filling or crown</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Dental abscess and facial swelling</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Trauma after a fall or sports injury</span></li>
                                    </ul>
                                </div>
                                <div className="bg-white rounded-2xl p-6 shadow-soft">
                                    <h3 className="text-lg font-semibold text-neutral-800 mb-3">While you're on your way:</h3>
                                    <ul className="space-y-2 text-neutral-700">
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Rinse with warm salt water</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Apply a cold compress for swelling</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Take over-the-counter pain relief if not contraindicated</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>If a tooth is knocked out, store it in milk and bring it immediately</span></li>
                                    </ul>
                                </div>
                            </div>
                            <div className="bg-primary/10 rounded-2xl p-6 text-center border border-primary/20">
                                <p className="text-lg text-neutral-700 mb-4">Greenvale is a straightforward drive to Broadmeadows, so call us and our reception team will look for the next available time, often the same day.</p>
                                <p className="text-lg text-neutral-700 mb-4">If you are coming from a Greenvale pocket that sits a little farther from the main roads, we can still help you plan the route and the timing so you arrive without stress. For severe pain or swelling, we will advise the fastest sensible next step and let you know whether you should come straight in.
                                </p>
                                <p className="text-lg text-neutral-700 mb-4">
                                    For parents especially, that clarity matters because emergency visits often happen at the busiest part of the week. We can tell you what to expect on arrival, how quickly we may need to move and what information will help us treat the problem efficiently, which is often enough to reduce the stress before you even get in the car.
                                </p>
                                <a href="tel:0390224442" className="inline-flex items-center justify-center gap-2 bg-primary text-white font-bold text-xl px-8 py-4 rounded-2xl hover:bg-primary/90 transition-colors">
                                    📞 (03) 9022 4442
                                </a>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-20 bg-white">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-6 text-center">
                                Cosmetic and Restorative Dentistry
                            </h2>
                            <p className="text-lg text-neutral-700 leading-relaxed mb-6 text-center">
                                Many Greenvale patients come in for cosmetic improvements or to repair worn, chipped or missing teeth. Every plan starts with a no-obligation consultation, photographs of your smile and a clearly written quote before any treatment begins.
                            </p>
                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="bg-neutral-50 rounded-2xl p-6 border border-primary/10">
                                    <h3 className="text-lg font-semibold text-primary mb-4">Cosmetic Treatments</h3>
                                    <ul className="space-y-2 text-neutral-700">
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>In-chair Zoom whitening</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Take-home whitening trays</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Porcelain veneers</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Minimally invasive bonding for chips and gaps</span></li>
                                    </ul>
                                </div>
                                <div className="bg-neutral-50 rounded-2xl p-6 border border-primary/10">
                                    <h3 className="text-lg font-semibold text-primary mb-4">Restorative Treatments</h3>
                                    <ul className="space-y-2 text-neutral-700">
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Ceramic crowns</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Dental bridges</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Root canal therapy</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Full-mouth rehabilitation</span></li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-20 bg-neutral-50">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-6 text-center">
                                Dental Implants and Full-Arch Rehabilitation
                            </h2>
                            <div className="bg-white rounded-2xl p-8 shadow-soft mb-6 space-y-6">
                                <p className="text-lg text-neutral-700 leading-relaxed">
                                    Missing teeth can affect chewing, jawbone health and the way your smile supports your face. Y3 Smiles offers permanent tooth replacement solutions for Greenvale patients who want a stable option rather than another removable appliance.
                                </p>
                                <div className="grid md:grid-cols-3 gap-4">
                                    <div className="bg-primary/5 rounded-xl p-4 text-center border border-primary/10">
                                        <div className="text-2xl font-bold text-primary mb-1">From $4,000</div>
                                        <div className="text-sm text-neutral-700">Single-tooth implant, all-inclusive</div>
                                    </div>
                                    <div className="bg-primary/5 rounded-xl p-4 text-center border border-primary/10">
                                        <div className="text-2xl font-bold text-primary mb-1">From $8,000</div>
                                        <div className="text-sm text-neutral-700">Implant-retained denture</div>
                                    </div>
                                    <div className="bg-primary/5 rounded-xl p-4 text-center border border-primary/10">
                                        <div className="text-2xl font-bold text-primary mb-1">From $24,500</div>
                                        <div className="text-sm text-neutral-700">All-on-X fixed bridge per arch</div>
                                    </div>
                                </div>
                                <p className="text-lg text-neutral-700 leading-relaxed">
                                    Dr Pavel — Senior Dentist with more than 26 years of experience — provides a free initial implant consultation, including a written quote and a treatment path that can be staged around your budget and timing.
                                </p>
                                <p className="text-lg text-neutral-700 leading-relaxed">
                                    For Greenvale patients who have lived with a gap for a long time, the consultation is a chance to talk honestly about function, aesthetics and timing. We can discuss whether you need a single implant, a multi-tooth solution or a more extensive full-arch plan, and we will make sure the financial and clinical steps are clear before anything begins.
                                </p>
                                <p className="text-lg text-neutral-700 leading-relaxed">
                                    That sort of clarity is especially helpful when you want to compare a longer-term implant option with a quicker temporary fix. We can map the path in plain language, explain how the treatment fits your bite and answer the practical questions that matter when you are making a decision for the next few years rather than the next few days.
                                </p>
                            </div>
                            <div className="text-center">
                                <Button variant="booking" size="lg" asChild>
                                    <Link to="/dental-implants-melbourne">
                                        <Star className="w-5 h-5 mr-2" />
                                        Book a Free Implant Consultation
                                    </Link>
                                </Button>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-20 bg-white">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-4 text-center">
                                Languages Spoken at Our Clinic
                            </h2>
                            <p className="text-lg text-neutral-700 leading-relaxed mb-8 text-center">
                                Greenvale is one of Melbourne's more family-oriented and multicultural suburbs, so we deliberately staff the clinic to make communication easy. If you would prefer to discuss treatment in your first language, just let us know when you book.
                            </p>
                            <div className="grid md:grid-cols-3 gap-6">
                                <div className="bg-primary/5 rounded-2xl p-6 text-center border border-primary/10">
                                    <div className="text-3xl mb-3">🇦🇺</div>
                                    <h3 className="text-lg font-semibold text-neutral-800 mb-2">English</h3>
                                    <p className="text-neutral-700 text-sm">All clinical and reception staff</p>
                                </div>
                                <div className="bg-primary/5 rounded-2xl p-6 text-center border border-primary/10">
                                    <div className="text-3xl mb-3">🇹🇷</div>
                                    <h3 className="text-lg font-semibold text-neutral-800 mb-2">Türkçe (Turkish)</h3>
                                    <p className="text-neutral-700 text-sm">Dr Fatima Kurnaz — bilingual principal dentist</p>
                                </div>
                                <div className="bg-primary/5 rounded-2xl p-6 text-center border border-primary/10">
                                    <div className="text-3xl mb-3">🌙</div>
                                    <h3 className="text-lg font-semibold text-neutral-800 mb-2">العربية (Arabic)</h3>
                                    <p className="text-neutral-700 text-sm">Arabic-speaking reception and clinical team members</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-20 bg-neutral-50">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-8 text-center">
                                Health Funds, Payment Plans and Pricing
                            </h2>
                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="bg-white rounded-2xl p-6 shadow-soft">
                                    <h3 className="text-lg font-semibold text-neutral-800 mb-4">Health Funds We Accept</h3>
                                    <p className="text-neutral-700 mb-3">All major Australian health funds with HICAPS instant claiming on-site, including:</p>
                                    <div className="flex flex-wrap gap-2 mb-3">
                                        {["Bupa", "Medibank Private", "HCF", "NIB", "CBHS", "AHM", "HBF", "Australian Unity", "Defence Health", "Police Health"].map((fund) => (
                                            <span key={fund} className="px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full">{fund}</span>
                                        ))}
                                    </div>
                                    <p className="text-sm text-neutral-700">NIB First Choice Network provider — NIB members receive reduced fees on many treatments.</p>
                                </div>
                                <div className="bg-white rounded-2xl p-6 shadow-soft">
                                    <h3 className="text-lg font-semibold text-neutral-800 mb-4">Payment Plans &amp; Options</h3>
                                    <ul className="space-y-3 text-neutral-700">
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Interest-free plans up to 12 months for treatments over $300</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>Extended low-interest plans up to 24 months</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>EFTPOS, Visa and Mastercard accepted</span></li>
                                        <li className="flex items-start"><CheckCircle className="w-5 h-5 text-primary mr-2 mt-0.5 flex-shrink-0" /><span>ATO Compassionate Release of Superannuation for clinically-required dental care</span></li>
                                    </ul>
                                    <p className="text-sm text-primary font-semibold mt-4">Every Greenvale patient receives a written quote before any treatment proceeds.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-20 bg-white">
                    <div className="container mx-auto px-6">
                        <div className="max-w-5xl mx-auto">
                            <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-10 text-center">
                                Meet Our Team
                            </h2>
                            <div className="grid md:grid-cols-3 gap-6">
                                <div className="bg-neutral-50 rounded-2xl p-6 border border-primary/10">
                                    <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-2xl flex items-center justify-center mb-4">
                                        <Users className="w-6 h-6 text-primary" />
                                    </div>
                                    <h3 className="text-lg font-bold text-neutral-800 mb-1">Dr Fatima Kurnaz</h3>
                                    <p className="text-sm text-primary font-medium mb-3">Principal Dentist &amp; Founder</p>
                                    <p className="text-sm text-neutral-700 leading-relaxed">DDS (University of Melbourne). AHPRA DEN0001925511. Bilingual Turkish/English. 11+ years experience. Special interests: cosmetic, emergency and implant dentistry.</p>
                                </div>
                                <div className="bg-neutral-50 rounded-2xl p-6 border border-primary/10">
                                    <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-2xl flex items-center justify-center mb-4">
                                        <Users className="w-6 h-6 text-primary" />
                                    </div>
                                    <h3 className="text-lg font-bold text-neutral-800 mb-1">Dr Pinar Geyik</h3>
                                    <p className="text-sm text-primary font-medium mb-3">Associate Dentist</p>
                                    <p className="text-sm text-neutral-700 leading-relaxed">BDS. AHPRA DEN0002905804. Calm, patient-centred care. Special interests: preventive, cosmetic and minor oral surgery.</p>
                                </div>
                                <div className="bg-neutral-50 rounded-2xl p-6 border border-primary/10">
                                    <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-2xl flex items-center justify-center mb-4">
                                        <Users className="w-6 h-6 text-primary" />
                                    </div>
                                    <h3 className="text-lg font-bold text-neutral-800 mb-1">Dr Javed Mahmud (Dr Pavel)</h3>
                                    <p className="text-sm text-primary font-medium mb-3">Senior Dentist</p>
                                    <p className="text-sm text-neutral-700 leading-relaxed">AHPRA DEN0001774450. 26+ years experience. Special interests: implants, oral surgery and restorative dentistry.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-20 bg-gradient-to-br from-neutral-50 to-primary/5 relative overflow-hidden">
                    <div className="container mx-auto px-6 relative z-10">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-10 text-center">
                                Why Greenvale Patients Choose Y3 Smiles Dental
                            </h2>
                            <div className="grid md:grid-cols-2 gap-4">
                                {greenvalePatientReasons.map((item, idx) => {
                                    const IconComponent = item.icon;
                                    return (
                                        <div key={idx} className="flex items-start gap-4 bg-white p-5 rounded-2xl shadow-soft border border-primary/5">
                                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                                <IconComponent className="w-5 h-5 text-primary" />
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-neutral-800 mb-1">{item.title}</h3>
                                                <p className="text-sm text-neutral-700">{item.desc}</p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-20 bg-white">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-3xl font-bold text-neutral-800 mb-6 text-center">Getting to Y3 Smiles Dental from Greenvale</h2>
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="bg-neutral-50 rounded-2xl p-6 shadow-soft">
                                    <h3 className="text-xl font-semibold text-neutral-800 mb-4 flex items-center gap-2">
                                        <MapPin className="w-5 h-5 text-primary" /> Address &amp; Parking
                                    </h3>
                                    <p className="text-neutral-700 mb-2">Unit 1, 34 King William Street, Broadmeadows VIC 3047</p>
                                    <p className="text-neutral-700 mb-4">Free on-site parking is available directly in front of the clinic.</p>
                                    <p className="text-neutral-700">
                                        About <strong>12 to 15 minutes' drive</strong> from Greenvale via Mickleham Road.
                                    </p>
                                </div>
                                <div className="bg-neutral-50 rounded-2xl p-6 shadow-soft">
                                    <h3 className="text-xl font-semibold text-neutral-800 mb-4">Public Transport</h3>
                                    <ul className="space-y-2 text-neutral-700">
                                        <li>🚆 <strong>Broadmeadows connection</strong> — take a bus or train toward Broadmeadows Station</li>
                                        <li>🚶 Short walk from Broadmeadows Station along King Street</li>
                                        <li>🚗 Easy access via Mickleham Road</li>
                                    </ul>
                                    <div className="mt-4">
                                        <a
                                            href="https://maps.app.goo.gl/XCeiAYziTDqGdJpc8"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-primary font-semibold hover:underline"
                                        >
                                            Get Directions on Google Maps →
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-16 bg-neutral-50">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-3xl font-bold text-neutral-800 mb-6 text-center">Local Landmarks Near Our Clinic</h2>
                            <div className="grid sm:grid-cols-2 gap-4">
                                {greenvaleLandmarks.map((landmark) => (
                                    <div key={landmark.title} className="flex items-start gap-3 bg-white p-4 rounded-xl border border-primary/10">
                                        <span className="text-2xl">{landmark.icon}</span>
                                        <div>
                                            <p className="font-semibold text-neutral-800">{landmark.title}</p>
                                            <p className="text-sm text-neutral-700">{landmark.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-20 bg-white">
                    <div className="container mx-auto px-6">
                        <div className="max-w-4xl mx-auto">
                            <h2 className="text-3xl font-bold text-neutral-800 mb-8 text-center">What Our Patients Say</h2>
                            <div className="grid md:grid-cols-3 gap-6">
                                {greenvaleTestimonials.map((testimonial, index) => (
                                    <div key={index} className="bg-neutral-50 rounded-2xl p-6 border border-primary/10">
                                        <div className="text-primary text-3xl mb-3">“</div>
                                        <p className="text-neutral-700 leading-relaxed mb-4 italic">{testimonial}</p>
                                        <p className="text-sm font-semibold text-primary">— Greenvale patient</p>
                                    </div>
                                ))}
                            </div>
                            <p className="text-lg text-neutral-700 leading-relaxed text-center mt-8 max-w-3xl mx-auto">
                                The questions below focus on the things Greenvale patients usually want to know before they book: the drive time, whether children can be bulk-billed, how emergency visits work, and what language or payment support is available. If you are weighing up a bigger treatment, the FAQ section should give you a useful starting point before you call.
                            </p>
                            <p className="text-lg text-neutral-700 leading-relaxed text-center mt-6 max-w-3xl mx-auto">
                                For Greenvale families, that kind of clarity reduces friction. A single clear explanation of the route, the parking, the fees and the likely treatment steps means you can plan the visit around school runs, work shifts and family commitments without a lot of back-and-forth. It also means the first conversation is about your actual needs rather than a generic list of services that does not account for where you live or how you travel.
                            </p>
                        </div>
                    </div>
                </section>

                <section className="py-20 bg-neutral-50">
                    <div className="container mx-auto px-6">
                        <div className="max-w-3xl mx-auto">
                            <h2 className="text-3xl font-bold text-neutral-800 mb-8 text-center">Frequently Asked Questions — Greenvale</h2>
                            <div className="space-y-4">
                                {greenvaleFaqItems.map((item, i) => (
                                    <div key={i} className="bg-white rounded-2xl p-6 border border-primary/10">
                                        <h3 className="text-lg font-semibold text-neutral-800 mb-2">{item.question}</h3>
                                        <p className="text-neutral-700 leading-relaxed">{item.answer}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <RelatedServiceLinks suburbName="Greenvale" suburbSlug="greenvale" className="bg-neutral-50" />

                <NearbySuburbs suburbSlug="greenvale" />

                <GoogleMap
                    locationName="Greenvale"
                    locationData={locationConfigs.greenvale.locationData}
                    openingHours={locationConfigs.greenvale.openingHours}
                    className="bg-primary/5"
                />
            </main>
            <Footer />
        </div>
    );
};

export default GreenvalePage;